// crewbit-admin/src/lib/taxMutations.ts
//
// Session 134 §Tax destination substrate — Money Module RPC-routed tax
// remittance mutations (admin / operator).
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Tax.5 + §Tax.6.
// Mirrors crewbit-mobile/lib/taxMutations.ts (Session 134 mobile half).
//
// Plain async functions, not a hook — they don't read or write React state.
// Identity (filed_by, business_id) resolves server-side via
// get_public_user_id() / get_user_business_id() inside the RPC bodies; the
// client never passes identity. The wrapper (callMoneyRpc) handles error
// mapping.
//
// `mark_remittance_filed` (migration 268) atomically writes the tax_remittances
// UPSERT + activity_log row in a single transaction. Idempotency check on
// confirmation_number precedes status guard per Decision #571 — replays of the
// same {business, tax_type, period, confirmation_number} return
// already_filed: true rather than REMITTANCE_ALREADY_FILED.
//
// Emit-via-inheritance per memory feedback_emit_via_inheritance.md:
// MONEY_MUTATION_EVENT emits at the wrapper boundary in markRemittanceFiled +
// reopenRemittance only (2 leaf emits). No inner wrapper to inherit from.
// Admin uses window.dispatchEvent (no DeviceEventEmitter equivalent).

import { callMoneyRpc, type MoneyRpcResult } from './moneyRpc';
import { MONEY_MUTATION_EVENT } from '../hooks/useFinancialSummary';

export type TaxType = 'hst' | 'gst' | 'qst' | 'pst' | 'income_tax' | 't4a' | 't5018';

export interface MarkRemittanceFiledArgs {
  businessId: string;
  taxType: TaxType;
  /** ISO date 'YYYY-MM-DD'. */
  periodStart: string;
  /** ISO date 'YYYY-MM-DD'. */
  periodEnd: string;
  confirmationNumber: string;
  amountCollected: number;
  amountItc: number;
  amountOwed: number;
  /** Defaults to now() server-side when null/omitted. */
  filedAt?: string | null;
  /** Quick Method only — pass-through for the row's audit fields. */
  quickMethodRemittanceRate?: number | null;
  quickMethodCredit?: number | null;
  notes?: string | null;
  /** Optional — passed through for forward-compat (RPC currently does not
   *  consume; idempotency is enforced via confirmation_number match). */
  idempotencyKey?: string | null;
}

export type MarkRemittanceFiledResult = MoneyRpcResult<{
  remittance_id: string;
  status: 'filed' | 'amended';
  already_filed: boolean;
}>;

export async function markRemittanceFiled(
  args: MarkRemittanceFiledArgs,
): Promise<MarkRemittanceFiledResult> {
  const result = await callMoneyRpc<{
    remittance_id: string;
    status: 'filed' | 'amended';
    already_filed: boolean;
  }>('mark_remittance_filed', {
    p_business_id: args.businessId,
    p_tax_type: args.taxType,
    p_period_start: args.periodStart,
    p_period_end: args.periodEnd,
    p_confirmation_number: args.confirmationNumber,
    p_amount_collected: args.amountCollected,
    p_amount_itc: args.amountItc,
    p_amount_owed: args.amountOwed,
    p_quick_method_remittance_rate: args.quickMethodRemittanceRate,
    p_quick_method_credit: args.quickMethodCredit,
    p_filed_at: args.filedAt,
    p_notes: args.notes,
    p_idempotency_key: args.idempotencyKey,
  });
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}

export interface ReopenRemittanceArgs {
  remittanceId: string;
  reason: string;
}

export type ReopenRemittanceResult = MoneyRpcResult<{
  remittance_id: string;
  status: 'reopened';
}>;

export async function reopenRemittance(
  args: ReopenRemittanceArgs,
): Promise<ReopenRemittanceResult> {
  const result = await callMoneyRpc<{
    remittance_id: string;
    status: 'reopened';
  }>('reopen_tax_remittance', {
    p_remittance_id: args.remittanceId,
    p_reason: args.reason,
  });
  if (result.success) {
    window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  }
  return result;
}
